import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, Form, Select, Space, Typography } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useClientsGetClient } from '@/api/generated/endpoints/clients/clients';
import {
  useShipmentsCreateShipment,
  useShipmentsUpdateShipment,
} from '@/api/generated/endpoints/shipments/shipments';
import type { ProductRef, ShipmentDetail } from '@/api/generated/model';
import { ApiProblem } from '@/api/http';
import { ClientSelect } from '@/components/ClientSelect';
import { ProductSelect } from '@/components/ProductSelect';
import { QtyInput } from '@/components/qty';
import { problemMessage } from '@/i18n/problem';
import { qtyRule, type QtyValue } from '@/lib/qty';
import { invalidatePath } from '@/lib/queryCache';
import { useIsMobile } from '@/lib/useIsMobile';

interface Line {
  /** Существующая строка — с id: сохраняет данные из УПД (источник, квант). */
  id?: number;
  product_id: number | null;
  product: ProductRef | null;
  qty: QtyValue;
}

interface Values {
  client_id: number | null;
  delivery_point_id: number | null;
  items: Line[];
}

/**
 * Черновик отгрузки: создание (shipment не задан) или правка (статус draft).
 * Количество — в единице товара строки; товар сменился — количество сбрасывается.
 */
export function DraftEditor({
  shipment,
  onSaved,
  onCancel,
}: {
  shipment?: ShipmentDetail;
  onSaved: (s: ShipmentDetail) => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();
  const mobile = useIsMobile();
  const [form] = Form.useForm<Values>();
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const clientId = Form.useWatch('client_id', form) as number | null | undefined;
  const lines = (Form.useWatch('items', form) as Line[] | undefined) ?? [];
  const client = useClientsGetClient(clientId ?? 0, { query: { enabled: !!clientId } });

  const handlers = {
    onSuccess: async (s: ShipmentDetail) => {
      await invalidatePath(queryClient, '/api/v1/shipments');
      onSaved(s);
    },
    onError: async (e: unknown) => {
      if (e instanceof ApiProblem && e.code === 'version_conflict') {
        await invalidatePath(queryClient, '/api/v1/shipments');
        setError(t('shipment.versionConflict'));
        return;
      }
      setError(problemMessage(e));
    },
  };
  const create = useShipmentsCreateShipment({ mutation: handlers });
  const update = useShipmentsUpdateShipment({ mutation: handlers });

  const initial: Values = shipment
    ? {
        client_id: shipment.client.id,
        delivery_point_id: shipment.delivery_point?.id ?? null,
        items: shipment.items.map((i) => ({
          id: i.id,
          product_id: i.product.id,
          product: i.product,
          qty: i.qty,
        })),
      }
    : { client_id: null, delivery_point_id: null, items: [] };

  function submit(v: Values) {
    setError(null);
    const items = v.items.map((l) => ({
      ...(l.id === undefined ? {} : { id: l.id }),
      product_id: l.product_id ?? 0,
      qty: typeof l.qty === 'string' ? l.qty : '0',
    }));
    if (shipment) {
      update.mutate({
        shipmentId: shipment.id,
        data: {
          version: shipment.version,
          client_id: v.client_id,
          delivery_point_id: v.delivery_point_id,
          items,
        },
      });
    } else {
      create.mutate({
        data: { client_id: v.client_id ?? 0, delivery_point_id: v.delivery_point_id, items },
      });
    }
  }

  const headerFields = (
    <>
      <Form.Item
        name="client_id"
        label={t('shipment.client')}
        rules={[{ required: true, message: t('shipment.clientRequired') }]}
      >
        <ClientSelect
          value={clientId ?? null}
          style={{ width: mobile ? '100%' : 320 }}
          onChange={(id) => {
            form.setFieldsValue({ client_id: id, delivery_point_id: null });
          }}
        />
      </Form.Item>
      <Form.Item name="delivery_point_id" label={t('shipment.point')}>
        <Select<number>
          allowClear
          style={{ width: mobile ? '100%' : 280 }}
          disabled={!clientId}
          options={(client.data?.delivery_points ?? [])
            .filter((p) => p.is_active)
            .map((p) => ({ value: p.id, label: p.name }))}
        />
      </Form.Item>
    </>
  );

  return (
    <Form<Values> form={form} layout="vertical" initialValues={initial} onFinish={submit}>
      {/* Телефон (T-177): поля друг под другом во всю ширину, без промежутков Space. */}
      {mobile ? (
        headerFields
      ) : (
        <Space size="large" wrap align="start">
          {headerFields}
        </Space>
      )}

      <Typography.Title level={5}>{t('shipment.lines')}</Typography.Title>
      <Form.List
        name="items"
        rules={[
          {
            validator: (_r, value: Line[] | undefined) =>
              value?.length
                ? Promise.resolve()
                : Promise.reject(new Error(t('shipment.linesRequired'))),
          },
        ]}
      >
        {(fields, { add, remove }, { errors }) => (
          <>
            {fields.map((field, index) => {
              const line = lines[index];
              const uom = line?.product?.base_uom ?? null;
              const product = (
                <Form.Item
                  name={[field.name, 'product_id']}
                  rules={[{ required: true, message: t('shipment.productRequired') }]}
                  style={{ width: mobile ? '100%' : 360, marginBottom: mobile ? 8 : undefined }}
                >
                  <ProductSelect
                    initial={line?.product}
                    onChange={(_, product) => {
                      form.setFieldValue(['items', field.name, 'product'], product);
                      form.setFieldValue(['items', field.name, 'qty'], null);
                    }}
                  />
                </Form.Item>
              );
              const qty = (
                <Form.Item
                  name={[field.name, 'qty']}
                  rules={[qtyRule({ required: true, positive: true })]}
                  style={mobile ? { marginBottom: 0 } : {}}
                >
                  <QtyInput
                    aria-label={`${t('shipment.colQty')} ${index + 1}`}
                    uom={uom ?? 'kg'}
                    disabled={uom === null}
                    style={{ width: mobile ? '100%' : 170 }}
                  />
                </Form.Item>
              );
              const hidden = (
                <Form.Item name={[field.name, 'product']} hidden>
                  <span />
                </Form.Item>
              );
              const removeButton = (
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  aria-label={t('shipment.removeLine')}
                  onClick={() => {
                    remove(field.name);
                  }}
                />
              );
              // Телефон (T-177): строка — карточкой, товар и количество во всю ширину.
              if (mobile) {
                return (
                  <Card
                    key={field.key}
                    size="small"
                    title={`${t('shipment.line')} ${index + 1}`}
                    extra={removeButton}
                    style={{ marginBottom: 12 }}
                  >
                    {product}
                    {hidden}
                    {qty}
                  </Card>
                );
              }
              return (
                <Space key={field.key} align="start" wrap>
                  <Typography.Text type="secondary" style={{ display: 'inline-block', width: 24 }}>
                    {index + 1}
                  </Typography.Text>
                  {product}
                  {hidden}
                  {qty}
                  {removeButton}
                </Space>
              );
            })}
            <Form.ErrorList errors={errors} />
            <Button
              icon={<PlusOutlined />}
              onClick={() => {
                add({ product_id: null, product: null, qty: null } satisfies Line);
              }}
              block={mobile}
              style={{ marginBottom: 16 }}
            >
              {t('shipment.addLine')}
            </Button>
          </>
        )}
      </Form.List>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      <Space direction={mobile ? 'vertical' : 'horizontal'} style={mobile ? { width: '100%' } : {}}>
        <Button
          type="primary"
          htmlType="submit"
          block={mobile}
          size={mobile ? 'large' : undefined}
          loading={create.isPending || update.isPending}
        >
          {shipment ? t('shipment.save') : t('shipment.create')}
        </Button>
        {onCancel && (
          <Button block={mobile} onClick={onCancel}>
            {t('shipment.cancelEdit')}
          </Button>
        )}
      </Space>
    </Form>
  );
}
